/**
 * Closed Jobs Report — Finance / Executive Management only.
 *
 * Replaces Pat Kross's quarterly manual workbook. Columns and GP%/variance
 * calcs match "Closed Jobs for a Period Report" (Sage Job Cost source).
 *
 * Features:
 *   - Quarter-to-date presets (Current Qtr, Prior Qtr, YTD, Trailing 12)
 *   - Date range filter with quick-select buttons
 *   - Filters: Category, Region, Salesman, PM
 *   - Free-text search across every table
 *   - Four tabs: By Category, By Salesman, By PM, All Jobs (detail)
 *   - Every column sortable (house rule via useSortableTable)
 *   - CSV export for current view + full job list detail
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Chip, Tabs, Tab,
  TextField, FormControl, InputLabel, Select, MenuItem, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, IconButton, Tooltip, OutlinedInput, Checkbox,
  ListItemText, InputAdornment, ButtonGroup,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Download as DownloadIcon, Refresh as RefreshIcon,
  FilterAlt as FilterIcon, Clear as ClearIcon,
  Search as SearchIcon, CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import {
  getClosedJobsFilters, getClosedJobsSummary,
  getClosedJobsByCategory, getClosedJobsBySalesman, getClosedJobsByPM,
  getClosedJobsList,
} from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';
import PageSearchBar from '../components/PageSearchBar';

// ─── Formatters ──────────────────────────────────────────────────────────────
const fmt$ = (n) => (n === null || n === undefined || n === '') ? '—' :
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtHrs = (n) => (n === null || n === undefined || n === '') ? '—' :
  `${Number(n).toFixed(0)} h`;
const fmtPct = (n) => (n === null || n === undefined || n === '') ? '—' :
  `${Number(n).toFixed(1)}%`;
const fmtDate = (d) => !d ? '—' :
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
const fmtShortDate = (d) => !d ? '—' :
  new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

// GP% color: green 50+, amber 25-50, red <25
const gpColor = (pct) => {
  const v = Number(pct || 0);
  if (v >= 50) return 'success.main';
  if (v >= 25) return 'warning.main';
  return 'error.main';
};

// ─── Quarter helpers ─────────────────────────────────────────────────────────
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function currentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return { from: new Date(now.getFullYear(), q * 3, 1), to: now };
}
function priorQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), (q - 1) * 3, 1);
  const end = new Date(now.getFullYear(), q * 3, 0); // last day of prior quarter
  return { from: start, to: end };
}
// FY (Fiscal Year-To-Date): ACS fiscal year starts October 1. If today is on
// or after Oct 1, FY started this calendar year; otherwise it started Oct 1 of
// the prior calendar year. This is the default "YTD" on this page — Pat Kross
// flagged the original calendar-YTD as wrong (2026-05-11). Cal YTD is offered
// separately below.
function ytd() {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: new Date(fyStartYear, 9, 1), to: now };
}
function calYtd() {
  const now = new Date();
  return { from: new Date(now.getFullYear(), 0, 1), to: now };
}
function trailing12() {
  const now = new Date();
  const from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  return { from, to: now };
}

// ─── CSV export ──────────────────────────────────────────────────────────────
function downloadCSV(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── MultiSelect ─────────────────────────────────────────────────────────────
function MultiSelect({ label, options, value, onChange, renderLabel }) {
  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(sel) => sel.length > 2 ? `${sel.length} selected` : sel.map(renderLabel || ((v) => v)).join(', ')}
      >
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.code;
          const lab = typeof opt === 'string' ? opt : (opt.label || opt.code);
          return (
            <MenuItem key={val} value={val}>
              <Checkbox checked={value.indexOf(val) > -1} />
              <ListItemText primary={lab} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

// ─── Free-text filter (client-side) ──────────────────────────────────────────
function filterBySearch(rows, search, keys) {
  const q = (search || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => keys.some((k) => {
    const v = r[k];
    return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
  }));
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ClosedJobs() {
  const [tab, setTab] = useState(0);
  const [filterOpts, setFilterOpts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [byCategory, setByCategory] = useState([]);
  const [bySalesman, setBySalesman] = useState([]);
  const [byPM, setByPM] = useState([]);
  const [jobsDetail, setJobsDetail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [category, setCategory] = useState([]);
  const [region, setRegion] = useState([]);
  const [salesman, setSalesman] = useState([]);
  const [pm, setPM] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activePreset, setActivePreset] = useState('qtr'); // default: current quarter
  const [search, setSearch] = useState('');

  // Initialize default date range = current quarter
  useEffect(() => {
    const { from, to } = currentQuarter();
    setDateFrom(ymd(from));
    setDateTo(ymd(to));
  }, []);

  const applyPreset = (key) => {
    let r;
    if (key === 'qtr') r = currentQuarter();
    else if (key === 'prior') r = priorQuarter();
    else if (key === 'ytd') r = ytd();
    else if (key === 'calytd') r = calYtd();
    else if (key === 't12') r = trailing12();
    else if (key === 'all') { setDateFrom(''); setDateTo(''); setActivePreset('all'); return; }
    if (r) {
      setDateFrom(ymd(r.from));
      setDateTo(ymd(r.to));
    }
    setActivePreset(key);
  };

  const queryParams = useMemo(() => {
    const p = {};
    if (category.length) p.category = category;
    if (region.length) p.region = region;
    if (salesman.length) p.salesman = salesman;
    if (pm.length) p.pm = pm;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [category, region, salesman, pm, dateFrom, dateTo]);

  // Detect manual date edits → clear preset highlight
  useEffect(() => {
    const q = currentQuarter(), pr = priorQuarter(), y = ytd(), cy = calYtd(), t = trailing12();
    if (!dateFrom && !dateTo && activePreset !== 'all') setActivePreset('all');
    else if (dateFrom === ymd(q.from) && dateTo === ymd(q.to)) setActivePreset('qtr');
    else if (dateFrom === ymd(pr.from) && dateTo === ymd(pr.to)) setActivePreset('prior');
    else if (dateFrom === ymd(y.from) && dateTo === ymd(y.to)) setActivePreset('ytd');
    else if (dateFrom === ymd(cy.from) && dateTo === ymd(cy.to)) setActivePreset('calytd');
    else if (dateFrom === ymd(t.from) && dateTo === ymd(t.to)) setActivePreset('t12');
    else setActivePreset(null);
    // eslint-disable-next-line
  }, [dateFrom, dateTo]);

  const loadFilters = async () => {
    try {
      const res = await getClosedJobsFilters();
      setFilterOpts(res.data);
    } catch (e) {
      if (e.response?.status === 403) setError('You do not have access to Closed Jobs (Finance / Executive Management only).');
      else setError('Failed to load filters.');
    }
  };

  const loadAggregates = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, sl, pp] = await Promise.all([
        getClosedJobsSummary(queryParams),
        getClosedJobsByCategory(queryParams),
        getClosedJobsBySalesman(queryParams),
        getClosedJobsByPM(queryParams),
      ]);
      setSummary(s.data);
      setByCategory(c.data.rows || []);
      setBySalesman(sl.data.rows || []);
      setByPM(pp.data.rows || []);
    } catch (e) {
      if (e.response?.status === 403) setError('You do not have access to Closed Jobs (Finance / Executive Management only).');
      else setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const loadJobsDetail = async () => {
    setLoadingJobs(true);
    try {
      const res = await getClosedJobsList({ ...queryParams, limit: 5000 });
      setJobsDetail(res.data.rows || []);
    } catch (e) {
      // non-fatal — aggregates still render
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { loadAggregates(); /* eslint-disable-next-line */ }, [queryParams]);
  // Load detail rows lazily when user switches to the All Jobs tab
  useEffect(() => {
    if (tab === 3 && jobsDetail.length === 0) loadJobsDetail();
    // eslint-disable-next-line
  }, [tab]);
  // Also reload detail whenever filters change AND user is on detail tab
  useEffect(() => {
    if (tab === 3) loadJobsDetail();
    // eslint-disable-next-line
  }, [queryParams]);

  const clearFilters = () => {
    setCategory([]); setRegion([]); setSalesman([]); setPM([]);
    setSearch('');
    applyPreset('qtr');
  };
  const hasFilters = category.length || region.length || salesman.length || pm.length || search;

  // ─── Filtered rows (per-tab search) ────────────────────────────────────────
  const catRowsFiltered = useMemo(
    () => filterBySearch(byCategory, search, ['job_type', 'category', 'category_label', 'region', 'region_label']),
    [byCategory, search]
  );
  const salRowsFiltered = useMemo(
    () => filterBySearch(bySalesman, search, ['salesman', 'key']),
    [bySalesman, search]
  );
  const pmRowsFiltered = useMemo(
    () => filterBySearch(byPM, search, ['pm_name', 'key']),
    [byPM, search]
  );
  const jobRowsFiltered = useMemo(
    () => filterBySearch(jobsDetail, search, [
      'primary_job_number', 'name', 'client_name', 'sage_job_type',
      'sage_salesman', 'sage_pm_name',
    ]),
    [jobsDetail, search]
  );

  // Sortable hooks
  const catTable = useSortableTable(catRowsFiltered, { defaultKey: 'total_contract', defaultDir: 'desc' });
  const salTable = useSortableTable(salRowsFiltered, { defaultKey: 'total_contract', defaultDir: 'desc' });
  const pmTable  = useSortableTable(pmRowsFiltered,  { defaultKey: 'total_contract', defaultDir: 'desc' });
  const jobTable = useSortableTable(jobRowsFiltered, { defaultKey: 'sage_est_comp', defaultDir: 'desc' });

  // ─── Export handlers ───────────────────────────────────────────────────────
  const exportCurrentTab = () => {
    if (tab === 0) {
      downloadCSV('closed-jobs-by-category.csv',
        ['Job Type', 'Region', 'Category', 'Jobs', 'Contract', 'Labor Est $', 'Labor Est Hrs',
         'Labor Actual $', 'Labor Actual Hrs', 'Labor %', 'Equip Est', 'Equip Actual',
         'Freight', 'Orig Profit', 'Orig GP%', 'Rev Profit', 'Rev GP%',
         'Labor Var Hrs', 'Labor Var $', 'Equip Var'],
        catTable.sortedRows.map((r) => [r.job_type, r.region_label, r.category_label,
          r.job_count, r.total_contract, r.total_labor_estimate_dollars, r.total_labor_estimate_hours,
          r.total_labor_actual_dollars, r.total_labor_actual_hours, r.labor_pct,
          r.total_equipment_estimate, r.total_equipment_actual, r.total_freight,
          r.total_orig_profit, r.orig_gp_pct, r.total_rev_profit, r.rev_gp_pct,
          r.labor_variance_hours, r.labor_variance_dollars, r.equipment_variance]));
    } else if (tab === 1) {
      downloadCSV('closed-jobs-by-salesman.csv',
        ['Salesman', 'Jobs', 'Contract', 'Labor Actual $', 'Labor Actual Hrs', 'Labor %',
         'Equip Actual', 'Freight', 'Rev Profit', 'Rev GP%'],
        salTable.sortedRows.map((r) => [r.salesman, r.job_count, r.total_contract,
          r.total_labor_actual_dollars, r.total_labor_actual_hours, r.labor_pct,
          r.total_equipment_actual, r.total_freight, r.total_rev_profit, r.rev_gp_pct]));
    } else if (tab === 2) {
      downloadCSV('closed-jobs-by-pm.csv',
        ['PM', 'Jobs', 'Contract', 'Labor Actual $', 'Labor Actual Hrs', 'Labor %',
         'Equip Actual', 'Freight', 'Rev Profit', 'Rev GP%'],
        pmTable.sortedRows.map((r) => [r.pm_name, r.job_count, r.total_contract,
          r.total_labor_actual_dollars, r.total_labor_actual_hours, r.labor_pct,
          r.total_equipment_actual, r.total_freight, r.total_rev_profit, r.rev_gp_pct]));
    } else if (tab === 3) {
      downloadCSV('closed-jobs-detail.csv',
        ['Job #', 'Salesman', 'PM', 'Description', 'Job Type', 'Date Closed',
         'Revised Contract', 'Labor $', 'Labor %', 'Equipment $', 'Freight',
         'Net Labor', 'Net Equip', 'Labor Est Hrs', 'Labor Est $', 'Equip Est',
         'Orig Profit $', 'Orig GP %', 'Labor Act Hrs', 'Labor Act $', 'Equip Act $',
         'Rev Profit $', 'Rev GP %', 'Var Labor Hrs', 'Var Labor $', 'Var Equip $'],
        jobTable.sortedRows.map((r) => [
          r.primary_job_number, r.sage_salesman, r.sage_pm_name, r.name, r.sage_job_type,
          r.sage_est_comp, r.sage_contract_amount, r.labor_actual_dollars, r.labor_pct,
          r.equipment_actual, r.freight, r.network_labor, r.network_equip,
          r.labor_estimate_hours, r.sage_labor_est, r.equipment_estimate,
          r.orig_profit_dollars, r.orig_profit_pct, r.labor_actual_hours,
          r.labor_actual_dollars, r.equipment_actual,
          r.rev_profit_dollars, r.rev_profit_pct,
          r.labor_variance_hours, r.labor_variance_dollars, r.equipment_variance]));
    }
  };

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <AccountBalanceIcon fontSize="large" sx={{ color: '#1565C0' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A5490' }}>Closed Jobs</Typography>
          <Typography variant="body2" color="text.secondary">
            Closed jobs from Sage. GP% and variance calculated from actuals vs estimates.
          </Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={() => { loadAggregates(); if (tab === 3) loadJobsDetail(); }}>
          <RefreshIcon /></IconButton></Tooltip>
      </Stack>

      {/* Summary strip */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Closed jobs</Typography>
              <Typography variant="h5">{summary.job_count || 0}</Typography></Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Contract total</Typography>
              <Typography variant="h5">{fmt$(summary.total_contract)}</Typography></Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Labor actual</Typography>
              <Typography variant="h5">{fmt$(summary.total_labor_actual_dollars)}</Typography>
              <Typography variant="caption">{fmtHrs(summary.total_labor_actual_hours)} · {fmtPct(summary.labor_pct)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Equipment actual</Typography>
              <Typography variant="h5">{fmt$(summary.total_equipment_actual)}</Typography></Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Revised profit</Typography>
              <Typography variant="h5">{fmt$(summary.total_rev_profit)}</Typography></Paper>
          </Grid>
          <Grid item xs={6} md={2}>
            <Paper sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Revised GP%</Typography>
              <Typography variant="h5" sx={{ color: gpColor(summary.rev_gp_pct) }}>{fmtPct(summary.rev_gp_pct)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Quarter preset + filter strip */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <CalendarIcon sx={{ color: 'text.secondary' }} />
          <ButtonGroup size="small" variant="outlined">
            <Button variant={activePreset === 'qtr' ? 'contained' : 'outlined'} onClick={() => applyPreset('qtr')}>Current Qtr</Button>
            <Button variant={activePreset === 'prior' ? 'contained' : 'outlined'} onClick={() => applyPreset('prior')}>Prior Qtr</Button>
            <Button variant={activePreset === 'ytd' ? 'contained' : 'outlined'} onClick={() => applyPreset('ytd')}>FYTD</Button>
            <Button variant={activePreset === 'calytd' ? 'contained' : 'outlined'} onClick={() => applyPreset('calytd')}>Cal YTD</Button>
            <Button variant={activePreset === 't12' ? 'contained' : 'outlined'} onClick={() => applyPreset('t12')}>Trailing 12</Button>
            <Button variant={activePreset === 'all' ? 'contained' : 'outlined'} onClick={() => applyPreset('all')}>All Time</Button>
          </ButtonGroup>
          <TextField type="date" label="From" size="small" slotProps={{ inputLabel: { shrink: true } }}
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 150 }} />
          <TextField type="date" label="To" size="small" slotProps={{ inputLabel: { shrink: true } }}
            value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ width: 150 }} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <PageSearchBar
            placeholder="Search within results…"
            value={search}
            onChange={(v) => setSearch(v)}
            onClear={() => setSearch('')}
          />
          <FilterIcon sx={{ color: 'text.secondary' }} />
          <MultiSelect label="Category" options={filterOpts?.categories || []} value={category} onChange={setCategory}
            renderLabel={(v) => (filterOpts?.categories.find((c) => c.code === v) || {}).label || v} />
          <MultiSelect label="Region" options={filterOpts?.regions || []} value={region} onChange={setRegion}
            renderLabel={(v) => (filterOpts?.regions.find((r) => r.code === v) || {}).label || v} />
          <MultiSelect label="Salesman" options={filterOpts?.salesmen || []} value={salesman} onChange={setSalesman} />
          <MultiSelect label="PM" options={filterOpts?.pms || []} value={pm} onChange={setPM} />
          {hasFilters && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>Clear filters</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={exportCurrentTab}>
            Export {tab === 0 ? 'category view' : tab === 1 ? 'salesman view' : tab === 2 ? 'PM view' : 'all jobs'}
          </Button>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`By Category (${catRowsFiltered.length})`} />
          <Tab label={`By Salesman (${salRowsFiltered.length})`} />
          <Tab label={`By PM (${pmRowsFiltered.length})`} />
          <Tab label={`All Jobs (${jobRowsFiltered.length || '—'})`} />
        </Tabs>

        {loading && tab !== 3 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── By Category ── */}
            {tab === 0 && (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {catTable.sortableCell('job_type', 'Job Type')}
                      {catTable.sortableCell('category_label', 'Category')}
                      {catTable.sortableCell('region_label', 'Region')}
                      {catTable.sortableCell('job_count', 'Jobs', 'right')}
                      {catTable.sortableCell('total_contract', 'Contract', 'right')}
                      {catTable.sortableCell('total_labor_actual_dollars', 'Labor $', 'right')}
                      {catTable.sortableCell('total_labor_actual_hours', 'Labor Hrs', 'right')}
                      {catTable.sortableCell('labor_pct', 'Labor %', 'right')}
                      {catTable.sortableCell('total_equipment_actual', 'Equip $', 'right')}
                      {catTable.sortableCell('total_freight', 'Freight', 'right')}
                      {catTable.sortableCell('total_orig_profit', 'Orig Profit', 'right')}
                      {catTable.sortableCell('orig_gp_pct', 'Orig GP%', 'right')}
                      {catTable.sortableCell('total_rev_profit', 'Rev Profit', 'right')}
                      {catTable.sortableCell('rev_gp_pct', 'Rev GP%', 'right')}
                      {catTable.sortableCell('labor_variance_hours', 'Var Hrs', 'right')}
                      {catTable.sortableCell('labor_variance_dollars', 'Var Labor $', 'right')}
                      {catTable.sortableCell('equipment_variance', 'Var Equip', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {catTable.sortedRows.length === 0 ? (
                      <TableRow><TableCell colSpan={17} align="center" sx={{ py: 4 }}>No data</TableCell></TableRow>
                    ) : catTable.sortedRows.map((r) => (
                      <TableRow key={r.job_type} hover>
                        <TableCell><Chip label={r.job_type || '—'} size="small" /></TableCell>
                        <TableCell>{r.category_label || '—'}</TableCell>
                        <TableCell>{r.region_label || '—'}</TableCell>
                        <TableCell align="right">{r.job_count}</TableCell>
                        <TableCell align="right">{fmt$(r.total_contract)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_labor_actual_dollars)}</TableCell>
                        <TableCell align="right">{fmtHrs(r.total_labor_actual_hours)}</TableCell>
                        <TableCell align="right">{fmtPct(r.labor_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_equipment_actual)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_freight)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_orig_profit)}</TableCell>
                        <TableCell align="right" sx={{ color: gpColor(r.orig_gp_pct), fontWeight: 600 }}>{fmtPct(r.orig_gp_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_rev_profit)}</TableCell>
                        <TableCell align="right" sx={{ color: gpColor(r.rev_gp_pct), fontWeight: 600 }}>{fmtPct(r.rev_gp_pct)}</TableCell>
                        <TableCell align="right" sx={{ color: r.labor_variance_hours >= 0 ? 'success.main' : 'error.main' }}>{Math.round(r.labor_variance_hours)}</TableCell>
                        <TableCell align="right" sx={{ color: r.labor_variance_dollars >= 0 ? 'success.main' : 'error.main' }}>{fmt$(r.labor_variance_dollars)}</TableCell>
                        <TableCell align="right" sx={{ color: r.equipment_variance >= 0 ? 'success.main' : 'error.main' }}>{fmt$(r.equipment_variance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── By Salesman ── */}
            {tab === 1 && (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {salTable.sortableCell('salesman', 'Salesman')}
                      {salTable.sortableCell('job_count', 'Jobs', 'right')}
                      {salTable.sortableCell('total_contract', 'Contract', 'right')}
                      {salTable.sortableCell('total_labor_actual_dollars', 'Labor $', 'right')}
                      {salTable.sortableCell('total_labor_actual_hours', 'Labor Hrs', 'right')}
                      {salTable.sortableCell('labor_pct', 'Labor %', 'right')}
                      {salTable.sortableCell('total_equipment_actual', 'Equip $', 'right')}
                      {salTable.sortableCell('total_freight', 'Freight', 'right')}
                      {salTable.sortableCell('total_rev_profit', 'Rev Profit', 'right')}
                      {salTable.sortableCell('rev_gp_pct', 'Rev GP%', 'right')}
                      {salTable.sortableCell('avg_contract', 'Avg Contract', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salTable.sortedRows.length === 0 ? (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>No data</TableCell></TableRow>
                    ) : salTable.sortedRows.map((r) => (
                      <TableRow key={r.salesman} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.salesman}</TableCell>
                        <TableCell align="right">{r.job_count}</TableCell>
                        <TableCell align="right">{fmt$(r.total_contract)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_labor_actual_dollars)}</TableCell>
                        <TableCell align="right">{fmtHrs(r.total_labor_actual_hours)}</TableCell>
                        <TableCell align="right">{fmtPct(r.labor_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_equipment_actual)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_freight)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_rev_profit)}</TableCell>
                        <TableCell align="right" sx={{ color: gpColor(r.rev_gp_pct), fontWeight: 600 }}>{fmtPct(r.rev_gp_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.avg_contract)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── By PM ── */}
            {tab === 2 && (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {pmTable.sortableCell('pm_name', 'PM')}
                      {pmTable.sortableCell('job_count', 'Jobs', 'right')}
                      {pmTable.sortableCell('total_contract', 'Contract', 'right')}
                      {pmTable.sortableCell('total_labor_actual_dollars', 'Labor $', 'right')}
                      {pmTable.sortableCell('total_labor_actual_hours', 'Labor Hrs', 'right')}
                      {pmTable.sortableCell('labor_pct', 'Labor %', 'right')}
                      {pmTable.sortableCell('total_equipment_actual', 'Equip $', 'right')}
                      {pmTable.sortableCell('total_freight', 'Freight', 'right')}
                      {pmTable.sortableCell('total_rev_profit', 'Rev Profit', 'right')}
                      {pmTable.sortableCell('rev_gp_pct', 'Rev GP%', 'right')}
                      {pmTable.sortableCell('avg_contract', 'Avg Contract', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pmTable.sortedRows.length === 0 ? (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>No data</TableCell></TableRow>
                    ) : pmTable.sortedRows.map((r) => (
                      <TableRow key={r.pm_name} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.pm_name}</TableCell>
                        <TableCell align="right">{r.job_count}</TableCell>
                        <TableCell align="right">{fmt$(r.total_contract)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_labor_actual_dollars)}</TableCell>
                        <TableCell align="right">{fmtHrs(r.total_labor_actual_hours)}</TableCell>
                        <TableCell align="right">{fmtPct(r.labor_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_equipment_actual)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_freight)}</TableCell>
                        <TableCell align="right">{fmt$(r.total_rev_profit)}</TableCell>
                        <TableCell align="right" sx={{ color: gpColor(r.rev_gp_pct), fontWeight: 600 }}>{fmtPct(r.rev_gp_pct)}</TableCell>
                        <TableCell align="right">{fmt$(r.avg_contract)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── All Jobs (detail) ── */}
            {tab === 3 && (
              <>
                {loadingJobs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 720 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {jobTable.sortableCell('primary_job_number', 'Job #')}
                          {jobTable.sortableCell('sage_salesman', 'Salesman')}
                          {jobTable.sortableCell('sage_pm_name', 'PM')}
                          {jobTable.sortableCell('name', 'Description')}
                          {jobTable.sortableCell('sage_job_type', 'Type')}
                          {jobTable.sortableCell('sage_est_comp', 'Closed', 'right')}
                          {jobTable.sortableCell('sage_contract_amount', 'Contract', 'right')}
                          {jobTable.sortableCell('labor_actual_dollars', 'Labor $', 'right')}
                          {jobTable.sortableCell('labor_pct', 'Lbr %', 'right')}
                          {jobTable.sortableCell('equipment_actual', 'Equip $', 'right')}
                          {jobTable.sortableCell('freight', 'Frt', 'right')}
                          {jobTable.sortableCell('labor_actual_hours', 'Lbr Hrs', 'right')}
                          {jobTable.sortableCell('orig_profit_dollars', 'Orig Prft', 'right')}
                          {jobTable.sortableCell('orig_profit_pct', 'Orig GP%', 'right')}
                          {jobTable.sortableCell('rev_profit_dollars', 'Rev Prft', 'right')}
                          {jobTable.sortableCell('rev_profit_pct', 'Rev GP%', 'right')}
                          {jobTable.sortableCell('labor_variance_hours', 'Var Hrs', 'right')}
                          {jobTable.sortableCell('labor_variance_dollars', 'Var Lbr$', 'right')}
                          {jobTable.sortableCell('equipment_variance', 'Var Eq$', 'right')}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobTable.sortedRows.length === 0 ? (
                          <TableRow><TableCell colSpan={19} align="center" sx={{ py: 4 }}>No data</TableCell></TableRow>
                        ) : jobTable.sortedRows.map((r, i) => (
                          <TableRow key={`${r.primary_job_number}-${i}`} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.primary_job_number}</TableCell>
                            <TableCell>{r.sage_salesman}</TableCell>
                            <TableCell>{r.sage_pm_name}</TableCell>
                            <TableCell sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <Tooltip title={r.name}><span>{r.name}</span></Tooltip>
                            </TableCell>
                            <TableCell><Chip label={r.sage_job_type || '—'} size="small" /></TableCell>
                            <TableCell align="right">{fmtShortDate(r.sage_est_comp)}</TableCell>
                            <TableCell align="right">{fmt$(r.sage_contract_amount)}</TableCell>
                            <TableCell align="right">{fmt$(r.labor_actual_dollars)}</TableCell>
                            <TableCell align="right">{fmtPct(r.labor_pct)}</TableCell>
                            <TableCell align="right">{fmt$(r.equipment_actual)}</TableCell>
                            <TableCell align="right">{fmt$(r.freight)}</TableCell>
                            <TableCell align="right">{fmtHrs(r.labor_actual_hours)}</TableCell>
                            <TableCell align="right">{fmt$(r.orig_profit_dollars)}</TableCell>
                            <TableCell align="right" sx={{ color: gpColor(r.orig_profit_pct) }}>{fmtPct(r.orig_profit_pct)}</TableCell>
                            <TableCell align="right">{fmt$(r.rev_profit_dollars)}</TableCell>
                            <TableCell align="right" sx={{ color: gpColor(r.rev_profit_pct), fontWeight: 600 }}>{fmtPct(r.rev_profit_pct)}</TableCell>
                            <TableCell align="right" sx={{ color: r.labor_variance_hours >= 0 ? 'success.main' : 'error.main' }}>{Math.round(r.labor_variance_hours || 0)}</TableCell>
                            <TableCell align="right" sx={{ color: r.labor_variance_dollars >= 0 ? 'success.main' : 'error.main' }}>{fmt$(r.labor_variance_dollars)}</TableCell>
                            <TableCell align="right" sx={{ color: r.equipment_variance >= 0 ? 'success.main' : 'error.main' }}>{fmt$(r.equipment_variance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {summary && summary.first_close && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  Period: {fmtDate(summary.first_close)} — {fmtDate(summary.last_close)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  GP%: <span style={{ color: '#2e7d32' }}>●</span> ≥50 &nbsp; <span style={{ color: '#ed6c02' }}>●</span> 25–50 &nbsp; <span style={{ color: '#d32f2f' }}>●</span> &lt;25
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
